import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, RotateCcw, Sparkles, ArrowUpRight,
  Clock, ShieldCheck, MessageCircle, Loader2,
} from "lucide-react";
import { API } from "@/lib/auth";
import { track } from "@/lib/analytics";

// ============================================================
// Find Your Perfect Hair Solution — dynamic quiz driven by backend.
// Steps: 0 = intro · 1..N = questions · N+1 = lead capture · N+2 = result
// ============================================================
export default function ExtensionsQuiz() {
  const [config, setConfig] = useState(null); // { questions, methods }
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({}); // qId -> answer_index
  const [step, setStep] = useState(0); // 0=intro
  const [lead, setLead] = useState({ name: "", email: "", phone: "", opt_in: false });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {top, second}
  const [startedAt, setStartedAt] = useState(null);

  useEffect(() => {
    axios.get(`${API}/quiz/config`)
      .then((r) => setConfig(r.data))
      .catch((err) => {
        console.warn("Failed to load quiz config", err);
        toast.error("Couldn't load the quiz — please refresh");
      })
      .finally(() => setLoading(false));
  }, []);

  const questions = config?.questions || [];
  const totalQ = questions.length;
  const totalSteps = totalQ + 2; // questions + lead + result
  const currentQuestionIdx = step - 1; // step 0 is intro
  const isIntro = step === 0;
  const isQuestion = step >= 1 && step <= totalQ;
  const isLead = step === totalQ + 1;
  const isResult = step === totalQ + 2;

  const progress = useMemo(() => {
    if (isIntro) return 0;
    if (isResult) return 100;
    const done = isLead ? totalQ + 1 : step;
    return Math.round((done / totalSteps) * 100);
  }, [step, totalQ, totalSteps, isIntro, isLead, isResult]);

  const startQuiz = async () => {
    setStartedAt(new Date().toISOString());
    setStep(1);
    try { track("quiz_start", {}); } catch (err) { console.warn("quiz_start track failed", err); }
    try { await axios.post(`${API}/quiz/start`, {}); } catch (err) { console.warn("quiz_start ping failed", err); }
  };

  const pick = (qId, idx) => {
    setAnswers((a) => ({ ...a, [qId]: idx }));
    setTimeout(() => setStep((s) => Math.min(s + 1, totalQ + 1)), 220);
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submitLead = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const missing = questions.filter((q) => answers[q.id] == null);
    if (missing.length > 0) {
      toast.error(`Please answer all ${totalQ} questions before viewing your result.`);
      setStep(questions.indexOf(missing[0]) + 1);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: lead.name.trim(),
        email: lead.email.trim(),
        phone: lead.phone.trim(),
        opt_in: !!lead.opt_in,
        started_at: startedAt,
        answers: questions.map((q) => ({
          question_id: q.id,
          answer_index: answers[q.id],
        })),
      };
      const r = await axios.post(`${API}/quiz/submit`, payload);
      setResult(r.data);
      setStep(totalQ + 2);
      try {
        track("quiz_complete", {
          top: r.data?.top?.slug,
          second: r.data?.second?.slug || null,
        });
      } catch (err) { console.warn("quiz_complete track failed", err); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your result — please try again");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setLead({ name: "", email: "", phone: "", opt_in: false });
    setResult(null);
    setStep(0);
    setStartedAt(null);
  };

  if (loading) {
    return (
      <div className="pt-40 pb-40 flex items-center justify-center text-[hsl(var(--onyx)/0.5)]">
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  if (!totalQ) {
    return (
      <div className="pt-40 pb-40 text-center text-[hsl(var(--onyx)/0.6)]">
        The quiz is being updated. Please check back shortly.
      </div>
    );
  }

  const question = questions[currentQuestionIdx];

  return (
    <div data-testid="page-extensions-quiz" className="pt-28 pb-24 min-h-[80vh] bg-gradient-to-b from-[hsl(var(--blush)/0.35)] via-white to-white">
      <div className="mx-auto max-w-[880px] px-5 sm:px-10">

        {/* Header — hidden on result */}
        {!isResult && (
          <div className="text-center">
            <div className="overline text-[hsl(var(--burgundy))] inline-flex items-center gap-2">
              <Sparkles size={12} /> Find your perfect hair solution
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight text-balance">
              {isIntro
                ? <>Which hair method is <span className="italic font-light">right for me?</span></>
                : <>Let's find your <span className="italic font-light">perfect match</span></>}
            </h1>
            {isIntro && (
              <p className="mt-5 text-[hsl(var(--onyx)/0.65)] max-w-xl mx-auto leading-relaxed">
                Answer a few quick questions and we'll recommend the extension or hair enhancement method best suited to your hair, lifestyle and goals.
              </p>
            )}
            <div className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
              <Clock size={14} /> Takes around 60 seconds
            </div>
          </div>
        )}

        {/* Progress bar */}
        {!isIntro && !isResult && (
          <div className="mt-10">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)] mb-3">
              <span data-testid="quiz-step-label">
                {isLead ? "Almost there — your details" : `Question ${currentQuestionIdx + 1} of ${totalQ}`}
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-[hsl(var(--onyx)/0.08)] overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--burgundy))] transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Intro */}
        {isIntro && <QuizIntro onStart={startQuiz} totalQ={totalQ} />}

        {/* Question */}
        {isQuestion && question && (
          <div key={question.id} className="mt-10 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <h2 className="font-display text-3xl sm:text-4xl leading-tight text-balance">
              {question.prompt}
            </h2>
            <div className="mt-8 grid gap-3">
              {(question.answers || []).map((opt, idx) => {
                const selected = answers[question.id] === idx;
                return (
                  <button
                    key={`${question.id}-${idx}`}
                    onClick={() => pick(question.id, idx)}
                    data-testid={`quiz-option-${question.order}-${idx}`}
                    className={`text-left w-full rounded-3xl border-2 px-6 py-5 transition-all duration-200 ${
                      selected
                        ? "border-[hsl(var(--burgundy))] bg-blush/60 shadow-[0_8px_30px_-12px_rgba(139,27,58,0.35)]"
                        : "border-[hsl(var(--onyx)/0.08)] hover:border-[hsl(var(--burgundy)/0.5)] bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="font-display text-lg sm:text-xl leading-tight">{opt.label}</div>
                      <div
                        className={`shrink-0 w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                          selected ? "border-[hsl(var(--burgundy))] bg-[hsl(var(--burgundy))]" : "border-[hsl(var(--onyx)/0.15)]"
                        }`}
                      >
                        {selected && <Check size={16} className="text-[hsl(var(--cream))]" />}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={back}
                data-testid="quiz-back"
                className="inline-flex items-center gap-2 text-sm text-[hsl(var(--onyx)/0.6)] hover:text-[hsl(var(--onyx))]"
              >
                <ArrowLeft size={14} /> {currentQuestionIdx === 0 ? "Back to intro" : "Previous question"}
              </button>
            </div>
          </div>
        )}

        {/* Lead capture */}
        {isLead && (
          <QuizLeadForm
            lead={lead}
            setLead={setLead}
            submitting={submitting}
            onBack={back}
            onSubmit={submitLead}
          />
        )}

        {/* Result */}
        {isResult && result && (
          <QuizResult result={result} lead={lead} onRestart={restart} />
        )}
      </div>
    </div>
  );
}


// ---------- Intro screen ----------
function QuizIntro({ onStart, totalQ }) {
  return (
    <div className="mt-14 text-center animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div className="mx-auto max-w-md rounded-[2.5rem] bg-white border border-[hsl(var(--onyx)/0.08)] shadow-[0_30px_80px_-40px_rgba(20,10,15,0.25)] px-8 py-10">
        <div className="w-16 h-16 mx-auto rounded-full bg-blush flex items-center justify-center">
          <Sparkles size={22} className="text-[hsl(var(--burgundy))]" />
        </div>
        <h3 className="font-display text-2xl mt-6">Ready when you are.</h3>
        <p className="text-sm text-[hsl(var(--onyx)/0.6)] mt-3 leading-relaxed">
          {totalQ} quick questions about your hair, goals and lifestyle — no jargon, no pressure.
          At the end we'll match you with the method our stylists would recommend.
        </p>
        <button
          onClick={onStart}
          data-testid="quiz-start"
          className="mt-8 btn-pill btn-primary w-full justify-center"
        >
          Start the quiz <ArrowRight size={16} />
        </button>
        <div className="mt-4 text-[11px] text-[hsl(var(--onyx)/0.5)] leading-relaxed">
          Your answers help our team prepare for your consultation. We'll never share them.
        </div>
      </div>
    </div>
  );
}


// ---------- Lead capture ----------
function QuizLeadForm({ lead, setLead, submitting, onBack, onSubmit }) {
  return (
    <form onSubmit={onSubmit} className="mt-10 animate-in fade-in slide-in-from-bottom-2 duration-300" data-testid="quiz-lead-form">
      <h2 className="font-display text-3xl sm:text-4xl leading-tight text-balance">
        One last thing — where should we send your result?
      </h2>
      <p className="mt-3 text-sm text-[hsl(var(--onyx)/0.6)] max-w-md">
        We'll email you your recommendation plus a link to book your complimentary consultation.
      </p>

      <div className="mt-8 grid gap-4">
        <label className="block">
          <span className="overline">Your name</span>
          <input
            type="text" required autoComplete="name"
            value={lead.name}
            onChange={(e) => setLead({ ...lead, name: e.target.value })}
            data-testid="quiz-lead-name"
            className="mt-2 w-full h-12 rounded-2xl bg-white border border-black/10 px-5 text-sm outline-none focus:border-[hsl(var(--burgundy))] transition-colors"
            placeholder="First and last name"
          />
        </label>
        <label className="block">
          <span className="overline">Email</span>
          <input
            type="email" required autoComplete="email"
            value={lead.email}
            onChange={(e) => setLead({ ...lead, email: e.target.value })}
            data-testid="quiz-lead-email"
            className="mt-2 w-full h-12 rounded-2xl bg-white border border-black/10 px-5 text-sm outline-none focus:border-[hsl(var(--burgundy))] transition-colors"
            placeholder="you@email.com"
          />
        </label>
        <label className="block">
          <span className="overline">Phone (for consultation callbacks)</span>
          <input
            type="tel" autoComplete="tel"
            value={lead.phone}
            onChange={(e) => setLead({ ...lead, phone: e.target.value })}
            data-testid="quiz-lead-phone"
            className="mt-2 w-full h-12 rounded-2xl bg-white border border-black/10 px-5 text-sm outline-none focus:border-[hsl(var(--burgundy))] transition-colors"
            placeholder="07…"
          />
        </label>
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer group">
        <input
          type="checkbox"
          checked={lead.opt_in}
          onChange={(e) => setLead({ ...lead, opt_in: e.target.checked })}
          data-testid="quiz-lead-optin"
          className="mt-1 w-5 h-5 rounded border-[hsl(var(--onyx)/0.3)] text-[hsl(var(--burgundy))] focus:ring-[hsl(var(--burgundy))]"
        />
        <span className="text-sm text-[hsl(var(--onyx)/0.75)] leading-relaxed">
          Yes, please add me to the Malek Dridi mailing list for salon exclusives, extension tips
          and new NøoksProRange drops. I understand I can unsubscribe at any time.
        </span>
      </label>

      <div className="mt-4 flex items-center gap-2 text-[11px] text-[hsl(var(--onyx)/0.5)]">
        <ShieldCheck size={12} /> Your data is stored securely and only used to prepare your consultation.
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onBack}
          data-testid="quiz-lead-back"
          className="btn-pill btn-outline order-2 sm:order-1"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="quiz-lead-submit"
          className="btn-pill btn-primary flex-1 justify-center order-1 sm:order-2 disabled:opacity-60"
        >
          {submitting ? <><Loader2 size={16} className="animate-spin" /> Preparing your match…</> : <>See my match <Sparkles size={16} /></>}
        </button>
      </div>
    </form>
  );
}


// ---------- Result page ----------
function QuizResult({ result, lead, onRestart }) {
  const { top, second } = result;
  const lowConfidence = (top?.score || 0) <= 2 && !second;

  // Build a pre-filled booking URL. Booking platforms typically accept
  // name/email/phone query params — we send both common variants so it
  // works across booking widgets (the-wee-nook.book.app in this case).
  const bookingParams = new URLSearchParams();
  if (lead?.name) {
    const [first, ...rest] = lead.name.trim().split(/\s+/);
    bookingParams.set("name", lead.name.trim());
    bookingParams.set("full_name", lead.name.trim());
    bookingParams.set("first_name", first || "");
    if (rest.length) bookingParams.set("last_name", rest.join(" "));
  }
  if (lead?.email) bookingParams.set("email", lead.email.trim());
  if (lead?.phone) bookingParams.set("phone", lead.phone.trim());
  if (top?.name) {
    bookingParams.set("notes", `Quiz match: ${top.name}${second ? ` · also considering ${second.name}` : ""}`);
    bookingParams.set("service", "Complimentary consultation");
  }
  const bookHref = `https://the-wee-nook.book.app?${bookingParams.toString()}`;
  const hasPrefill = !!(lead?.name && lead?.email);
  return (
    <div data-testid="quiz-result" className="mt-6 animate-in fade-in slide-in-from-bottom-3 duration-500">
      <div className="text-center">
        <div className="overline text-[hsl(var(--burgundy))] inline-flex items-center gap-2">
          <Sparkles size={12} /> Your perfect match
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 tracking-tight text-balance">
          {second
            ? <>Two beautiful matches, <span className="italic font-light">tailored to you.</span></>
            : <>{top.name}</>}
        </h1>
        {second && (
          <p className="mt-4 text-[hsl(var(--onyx)/0.65)] max-w-xl mx-auto leading-relaxed">
            Based on your answers, both of these methods could suit you beautifully. Book a
            complimentary consultation and we'll help you choose the one that fits you best.
          </p>
        )}
      </div>

      <div className={`mt-12 grid gap-8 ${second ? "lg:grid-cols-2" : ""}`}>
        <MethodCard method={top} primary />
        {second && <MethodCard method={second} />}
      </div>

      {lowConfidence && (
        <div className="mt-8 rounded-3xl border border-[hsl(var(--gold)/0.4)] bg-white p-6 sm:p-8 text-sm text-[hsl(var(--onyx)/0.75)] leading-relaxed">
          <div className="overline text-[hsl(var(--gold))]">Not quite sure?</div>
          <p className="mt-3">
            Your answers didn't lean strongly towards any one method — that's completely normal.
            The best next step is a complimentary in-person consultation where we can look at your
            hair, discuss your goals and recommend exactly the right approach for you.
          </p>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-12 rounded-3xl bg-blush/50 border border-[hsl(var(--onyx)/0.06)] p-6 sm:p-8 text-sm text-[hsl(var(--onyx)/0.75)] leading-relaxed">
        <div className="overline text-[hsl(var(--gold))]">A note from Malek</div>
        <p className="mt-3">
          This quiz is designed as a helpful guide only. Every head of hair is unique, so we always
          recommend booking a complimentary consultation where we can assess your natural hair,
          discuss your goals and create a completely bespoke recommendation that's perfect for you.
        </p>
      </div>

      {/* Final CTA — one-click booking with pre-filled details */}
      <div className="mt-12 rounded-3xl bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] p-8 sm:p-12 text-center">
        <div className="overline text-[hsl(var(--gold))]">Reserve your slot in one tap</div>
        <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-balance">
          {lead?.name ? `${lead.name.split(/\s+/)[0]}, ` : ""}book your complimentary consultation.
        </h2>
        <p className="mt-4 text-[hsl(var(--cream)/0.7)] max-w-xl mx-auto text-sm leading-relaxed">
          {hasPrefill ? (
            <>We'll send your quiz answers ahead so Malek knows exactly what to prepare for you.</>
          ) : (
            <>Whether you're looking for longer hair, more volume or support with hair loss, we're here to help.</>
          )}
        </p>

        {hasPrefill && (
          <div
            data-testid="quiz-booking-prefill"
            className="mt-6 mx-auto max-w-md rounded-2xl bg-[hsl(var(--cream)/0.08)] border border-[hsl(var(--cream)/0.15)] p-5 text-left space-y-2 text-xs"
          >
            <div className="overline text-[hsl(var(--gold))] mb-2">Pre-filled for you</div>
            <PrefillRow label="Name" value={lead.name} />
            <PrefillRow label="Email" value={lead.email} />
            {lead.phone && <PrefillRow label="Phone" value={lead.phone} />}
            <PrefillRow label="Interested in" value={top?.name + (second ? ` or ${second.name}` : "")} />
          </div>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={bookHref}
            target="_blank"
            rel="noreferrer"
            data-testid="quiz-cta-book"
            className="btn-pill btn-blush"
          >
            {hasPrefill ? "Reserve my slot" : "Book my complimentary consultation"} <ArrowUpRight size={16} />
          </a>
          <Link
            to="/contact"
            data-testid="quiz-cta-message"
            className="btn-pill bg-transparent border border-[hsl(var(--cream)/0.3)] text-[hsl(var(--cream))] hover:bg-[hsl(var(--cream)/0.08)]"
          >
            <MessageCircle size={16} /> Message our team
          </Link>
        </div>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={onRestart}
          data-testid="quiz-restart"
          className="inline-flex items-center gap-2 text-sm text-[hsl(var(--onyx)/0.6)] hover:text-[hsl(var(--onyx))] underline underline-offset-4"
        >
          <RotateCcw size={14} /> Retake the quiz
        </button>
      </div>
    </div>
  );
}


function MethodCard({ method, primary = false }) {
  return (
    <div
      data-testid={`quiz-method-${method.slug}`}
      className="rounded-[2.5rem] bg-white border border-[hsl(var(--onyx)/0.08)] shadow-[0_30px_80px_-40px_rgba(20,10,15,0.25)] overflow-hidden"
    >
      <div className="aspect-[4/3] bg-blush">
        {method.image && (
          <img src={method.image} alt={method.name} className="w-full h-full object-cover" loading="lazy" />
        )}
      </div>
      <div className="p-7 sm:p-8">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--burgundy))]">
          {primary ? <><Sparkles size={12} /> Top match</> : <>Also recommended</>}
        </div>
        {method.tag && <div className="overline mt-3">{method.tag}</div>}
        <h3 className="font-display text-3xl mt-2 leading-tight text-balance">{method.name}</h3>
        <p className="mt-4 text-[hsl(var(--onyx)/0.7)] leading-relaxed text-sm">{method.why}</p>

        {method.benefits?.length > 0 && (
          <ul className="mt-5 space-y-2">
            {method.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-[hsl(var(--onyx)/0.75)]">
                <div className="shrink-0 mt-1 w-4 h-4 rounded-full bg-[hsl(var(--burgundy))] flex items-center justify-center">
                  <Check size={10} className="text-[hsl(var(--cream))]" />
                </div>
                {b}
              </li>
            ))}
          </ul>
        )}

        <dl className="mt-6 grid grid-cols-2 gap-4 text-xs">
          {method.maintenance && (
            <div>
              <dt className="overline text-[hsl(var(--gold))]">Maintenance</dt>
              <dd className="mt-1 text-[hsl(var(--onyx)/0.75)]">{method.maintenance}</dd>
            </div>
          )}
          {method.appointment_time && (
            <div>
              <dt className="overline text-[hsl(var(--gold))]">Appointment</dt>
              <dd className="mt-1 text-[hsl(var(--onyx)/0.75)]">{method.appointment_time}</dd>
            </div>
          )}
        </dl>

        <div className="mt-7 flex flex-wrap gap-2">
          {method.pricing_url && (
            <Link
              to={method.pricing_url}
              data-testid={`quiz-method-pricing-${method.slug}`}
              className="btn-pill btn-outline text-xs"
            >
              View pricing <ArrowUpRight size={14} />
            </Link>
          )}
          {method.aftercare_url && (
            <Link
              to={method.aftercare_url}
              data-testid={`quiz-method-aftercare-${method.slug}`}
              className="btn-pill btn-outline text-xs"
            >
              Aftercare info
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}


function PrefillRow({ label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-16 shrink-0 text-[hsl(var(--cream)/0.5)] uppercase tracking-widest">{label}</div>
      <div className="text-[hsl(var(--cream))] truncate">{value}</div>
      <Check size={12} className="ml-auto text-[hsl(var(--gold))]" />
    </div>
  );
}
